import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import SearchIcon from "@mui/icons-material/Search";
import { LoadingButton as MuiButton } from "@mui/lab";
import { Box } from "@mui/material";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import AccountIcon from "../../assets/DesktopNavBox/AccountIcon";
import LocationIcon from "../../assets/Icons/LocationIcon";
import VerifiedIcon from "../../assets/Icons/VerifiedIcon";
import BrowseType from "../../constants/BrowseType";
import Tabs from "../../constants/tabs";
import UserType from "../../constants/UserType";
import { fetchChatCardData, getChatRoomId } from "../../firebase/chat";
import { ChatCardData } from "../../interfaces/Message";
import User from "../../interfaces/User";
import { theme } from "../../mui-customs/Theme";
import { setActiveTab } from "../../redux/createBrowsePageSlice";
import { setTab } from "../../redux/createTabSlice";
import {
  setSelectedChatId,
  setSelectedReceiverData,
} from "../../redux/messagingSlice";
import { RootState } from "../../redux/store";
import { STRIPE_ACCOUNT_STATUS } from "../../types/enums";
import Button from "../Button";
import ExpandableText from "../ExpandableText";
import LikeAndUnLikeChef from "../LikeAndUnLikeChef";
import ProfileImage from "../profile/ProfileImage";
import ServicesDisplayTag from "../profile/ServicesDisplayTag";
import ReviewDisplayTag from "../ReviewDisplayTag";
import Typography from "../Typography";
import { HourlyRateIcon } from "./HourlyRateIcon";

const ProfileSideBar = () => {
  const userData = useSelector((state: RootState) => state.user.user);
  const [canEdit, setCanEdit] = useState(true);
  const [isCreatingChatRoom, setIsCreatingChatRoom] = useState(false);

  const selectedSidebarUser = useSelector(
    (state: RootState) => state.sidebarState.selectedSidebarUser
  );

  const [sidebarUserData, setSidebarUserData] = useState<User>(userData);

  useEffect(() => {
    userData && !selectedSidebarUser && setSidebarUserData(userData);
  }, [userData]);

  const handleSendMessage = async () => {
    if (sidebarUserData.uid && userData.uid) {
      setIsCreatingChatRoom(true);
      const chatRoomId: string = await getChatRoomId(
        userData.uid,
        sidebarUserData.uid
      );
      const receiverData: ChatCardData = await fetchChatCardData(
        chatRoomId,
        userData.uid
      );
      dispatch(setSelectedReceiverData(receiverData));
      dispatch(setSelectedChatId(chatRoomId));
      setIsCreatingChatRoom(false);
      router.push("/message");
    }
  };

  useEffect(() => {
    if (selectedSidebarUser && userData.uid !== selectedSidebarUser.uid) {
      setSidebarUserData(selectedSidebarUser);
      setCanEdit(false);
    } else {
      setSidebarUserData(userData);
      setCanEdit(true);
    }
  }, [selectedSidebarUser]);

  const router = useRouter();
  const dispatch = useDispatch();

  return (
    <>
      {sidebarUserData.uid ? (
        <Box
          sx={{
            p: 3,
            pt: 4,
            backgroundColor: theme.whiteBg.WBG1,
            borderRadius: "8px",
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              alignItems: "center",
              gap: 1,
            }}
          >
            {sidebarUserData.uid && (
              <ProfileImage
                userData={sidebarUserData}
                canEdit={canEdit}
                sx={{ mb: 2 }}
              />
            )}
            <Typography variant="h2" sx={{ color: theme.black[800] }}>
              {sidebarUserData.publicName
                ? `${sidebarUserData.publicName}`
                : `${sidebarUserData.firstName} ${
                    sidebarUserData.lastName && sidebarUserData.lastName[0]
                  }`}
            </Typography>
            {sidebarUserData.pronoun !== "Other" && (
              <Typography variant="body2" sx={{ color: theme.grey2[300] }}>
                {sidebarUserData.pronoun}
              </Typography>
            )}
            {sidebarUserData.tagline && (
              <Typography
                variant="body2"
                sx={{ color: theme.grey2[300], textAlign: "center", mt: 2 }}
              >
                {sidebarUserData.tagline}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-evenly",
              alignItems: "flex-end",
              width: "100%",
            }}
          >
            {sidebarUserData.hourlyRate && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "14px",
                }}
              >
                <HourlyRateIcon
                  hourlyRate={parseInt(sidebarUserData.hourlyRate)}
                />
                <Typography variant="title2" sx={{ color: theme.grey2[400] }}>
                  {`$${
                    sidebarUserData.hourlyRate
                      ? sidebarUserData.hourlyRate
                      : " None"
                  }/hour`}
                </Typography>
              </Box>
            )}
            <ReviewDisplayTag rating={sidebarUserData.rating} />
            {!canEdit &&
              sidebarUserData.userType === UserType.Chef &&
              sidebarUserData.uid &&
              userData.userType === UserType.Client && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <LikeAndUnLikeChef chefUid={sidebarUserData.uid} />
                  <Typography variant="title2" sx={{ color: theme.grey2[400] }}>
                    Save
                  </Typography>
                </Box>
              )}
          </Box>
          {userData.uid ? (
            <Box>
              <MuiButton
                onClick={() => {
                  router.push(`/profile-public/${sidebarUserData.uid}`);
                }}
                variant="contained"
                startIcon={<AccountIcon />}
                sx={{
                  textTransform: "none",
                  py: "1rem",
                  color: theme.whiteBg.WBG1,
                  width: "100%",
                }}
              >
                <Typography variant="btnMedium">View Profile</Typography>
              </MuiButton>
              {!canEdit ? (
                <MuiButton
                  variant="outlined"
                  loading={isCreatingChatRoom}
                  startIcon={
                    !isCreatingChatRoom && (
                      <NotificationsNoneRoundedIcon
                        sx={{ color: theme.brand1[500] }}
                      />
                    )
                  }
                  sx={{
                    textTransform: "none",
                    py: "1rem",
                    width: "100%",
                    mt: "2rem",
                  }}
                  onClick={handleSendMessage}
                >
                  <Typography variant="btnMedium">Message</Typography>
                </MuiButton>
              ) : (
                <MuiButton
                  variant="outlined"
                  startIcon={<SearchIcon sx={{ color: theme.brand1[500] }} />}
                  sx={{
                    textTransform: "none",
                    py: "1rem",
                    width: "100%",
                    mt: "2rem",
                  }}
                  onClick={() => {
                    router.push("/browse");
                    dispatch(
                      setActiveTab(
                        sidebarUserData.userType === UserType.Chef
                          ? BrowseType.Gig
                          : BrowseType.Chef
                      )
                    );
                    dispatch(setTab(Tabs.Home));
                  }}
                >
                  <Typography variant="btnMedium">{`Browse ${
                    sidebarUserData.userType === UserType.Chef
                      ? "Gigs"
                      : "Chefs"
                  }`}</Typography>
                </MuiButton>
              )}
            </Box>
          ) : (
            <Box sx={{ backgroundColor: "#EDF4F0", p: 3 }}>
              <Typography
                variant="body5"
                sx={{ color: theme.black[800], textAlign: "center", mb: 2 }}
              >
                {`Sign up to discuss your 
project with ${sidebarUserData.publicName}`}
              </Typography>
              <Button
                onClick={() => {
                  router.push("/auth/sign-up");
                }}
              >
                Sign Up
              </Button>
            </Box>
          )}
          {sidebarUserData.bio && (
            <Box>
              <Typography variant="verySmall2" sx={{ color: theme.grey2[300] }}>
                Bio
              </Typography>
              <ExpandableText
                sx={{ mt: 1 }}
                text={sidebarUserData.bio}
                charLimit={70}
              />
            </Box>
          )}
          {sidebarUserData.chefServices && (
            <Box>
              <Typography variant="verySmall2" sx={{ color: theme.grey2[300] }}>
                Services I offer
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
                {sidebarUserData.chefServices.map((each, index) => {
                  return <ServicesDisplayTag key={index} value={each} />;
                })}
              </Box>
            </Box>
          )}
          {sidebarUserData.foodPreferences && (
            <Box>
              <Typography variant="verySmall2" sx={{ color: theme.grey2[300] }}>
                {sidebarUserData.userType === UserType.Chef
                  ? "Chef Specialties"
                  : "Food Preferences"}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
                {sidebarUserData.foodPreferences.map((each, index) => {
                  return <ServicesDisplayTag key={index} value={each} />;
                })}
              </Box>
            </Box>
          )}
          {sidebarUserData.address && (
            <Box>
              <Typography variant="verySmall2" sx={{ color: theme.grey2[300] }}>
                Location
              </Typography>
              <Box sx={{ display: "flex", gap: 2, alignItems: "start", mt: 2 }}>
                <LocationIcon dimension={30} />
                <Typography
                  variant="body5"
                  sx={{ color: theme.black[800], flex: "1" }}
                >
                  {`${sidebarUserData.address?.locality}, ${sidebarUserData.address?.state}`}
                </Typography>
              </Box>
            </Box>
          )}
          <Box>
            <Typography variant="verySmall2" sx={{ color: theme.grey2[300] }}>
              Verifications
            </Typography>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", mt: 2 }}>
              {sidebarUserData.stripe_account_status ===
              STRIPE_ACCOUNT_STATUS.SUCCESSFUL ? (
                <>
                  <VerifiedIcon />
                  <Typography variant="body5" sx={{ color: theme.black[800] }}>
                    ID: Verified
                  </Typography>
                </>
              ) : (
                <>
                  <ErrorRoundedIcon
                    sx={{ color: theme.grey1[400], fontSize: 29 }}
                  />
                  <Typography variant="body5" sx={{ color: theme.black[800] }}>
                    ID: Not Verified
                  </Typography>
                </>
              )}
            </Box>
          </Box>
          {sidebarUserData.culinaryTraining && (
            <Box>
              <Typography variant="verySmall2" sx={{ color: theme.grey2[300] }}>
                Culinary Experience
              </Typography>
              <ExpandableText
                sx={{ mt: 2 }}
                text={sidebarUserData.culinaryTraining}
                charLimit={70}
              />
            </Box>
          )}
          {sidebarUserData.otherTraining && (
            <Box>
              <Typography variant="verySmall2" sx={{ color: theme.grey2[300] }}>
                Other Training
              </Typography>
              <ExpandableText
                sx={{ mt: 2 }}
                text={sidebarUserData.otherTraining}
                charLimit={70}
              />
            </Box>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            p: 3,
            pt: 4,
            backgroundColor: theme.whiteBg.WBG1,
            borderRadius: "8px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: 4,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              width: "100%",
            }}
          >
            <Button
              disabled={Boolean(userData.uid)}
              onClick={() => {
                router.push("/auth/login");
              }}
            >
              Login
            </Button>
            <Button
              disabled={Boolean(userData.uid)}
              onClick={() => {
                router.push("/auth/sign-up");
              }}
            >
              Sign up
            </Button>
          </Box>
        </Box>
      )}
    </>
  );
};

export default ProfileSideBar;
